import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaFutbol, FaListOl, FaStar, FaUsers } from "react-icons/fa";
import {
  Button,
  Card,
  InputNumber,
  Message,
  Modal,
  Spin,
  Tabs,
  Tag,
} from "@/frontend/components/common";
import {
  bettingPoolService,
  fixtureService,
  poolPredictionService,
  tournamentStandingService,
} from "@/frontend/services";
import { useAuthStore } from "@/frontend/stores";
import { PoolLeaderboardTab, PoolRulesTab, PoolCommentsTab } from "./components";
import type {
  BettingPoolWithTournamentResponseDTO,
  FixtureWithPredictionResponseDTO,
  PoolPredictionResponseDTO,
  TournamentStandingGroupedDTO,
} from "@/shared/dto";

interface PredictionInput {
  home: number | null;
  away: number | null;
}

type FilterTab = "live" | "pending" | "finished" | "all";
type MainTab = "predictions" | "groups" | "leaderboard" | "rules" | "comments";

export function PoolPredictionsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user: currentUser } = useAuthStore();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [pool, setPool] = useState<BettingPoolWithTournamentResponseDTO | null>(null);
  const [fixtures, setFixtures] = useState<FixtureWithPredictionResponseDTO[]>([]);
  const [groups, setGroups] = useState<TournamentStandingGroupedDTO[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [predictionIds, setPredictionIds] = useState<Record<string, string>>({});
  const [inputs, setInputs] = useState<Record<string, PredictionInput>>({});
  const [nextCursor, setNextCursor] = useState<{ cursorDate: string | null; cursorId: string } | null>(null);
  const [hasNext, setHasNext] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [activeTab, setActiveTab] = useState<FilterTab>("all");
  const [mainTab, setMainTab] = useState<MainTab>("predictions");
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);

  const [viewingFixture, setViewingFixture] = useState<FixtureWithPredictionResponseDTO | null>(null);
  const [othersPredictions, setOthersPredictions] = useState<PoolPredictionResponseDTO[]>([]);
  const [loadingOthers, setLoadingOthers] = useState(false);

  const loadInitial = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);

      const [poolResponse, fixturesResponse, predictionsResponse] = await Promise.all([
        bettingPoolService.getByIdWithTournament(id),
        fixtureService.getWithPredictions({ poolId: id, pageSize: 120, initialLoad: true }),
        poolPredictionService.getByPool(id),
      ]);

      if (poolResponse.success && poolResponse.data) {
        setPool(poolResponse.data);
        const seasonId = poolResponse.data.season?.id;
        if (seasonId) {
          setLoadingGroups(true);
          try {
            const standingsResponse = await tournamentStandingService.getBySeasonId(seasonId, {
              grouped: true,
            });
            if (standingsResponse.success && standingsResponse.data) {
              setGroups(normalizeGroupedStandings(standingsResponse.data));
            } else {
              setGroups([]);
            }
          } catch {
            setGroups([]);
            Message.error({ content: "Error al cargar los grupos" });
          } finally {
            setLoadingGroups(false);
          }
        }
      }

      if (predictionsResponse.success && predictionsResponse.data) {
        const map: Record<string, string> = {};
        for (const prediction of predictionsResponse.data) {
          map[prediction.fixtureId] = prediction.id;
        }
        setPredictionIds(map);
      }

      if (fixturesResponse.success && fixturesResponse.data) {
        setFixtures(fixturesResponse.data.data);
        setInputs(buildInputs(fixturesResponse.data.data));
        setHasNext(fixturesResponse.data.pagination.hasNext);
        setNextCursor(fixturesResponse.data.pagination.nextCursor ?? null);

        const hasLive = fixturesResponse.data.data.some((f) => f.status.isInProgress);
        const hasPendingToday = fixturesResponse.data.data.some(
          (f) => isPendingFixture(f) && isToday(f.date),
        );
        if (hasPendingToday) {
          setActiveTab("pending");
        } else if (hasLive) {
          setActiveTab("live");
        } else {
          setActiveTab("all");
        }
      } else {
        Message.error({
          content: fixturesResponse.error || "Error al cargar los partidos",
        });
      }
    } catch (error) {
      Message.error({
        content: error instanceof Error ? error.message : "Error al cargar el grupo",
      });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadInitial();
  }, [loadInitial]);

  const buildInputs = (items: FixtureWithPredictionResponseDTO[]) => {
    const map: Record<string, PredictionInput> = {};
    for (const fixture of items) {
      const prediction = fixture.userPrediction.prediction;
      map[fixture.matchId] = {
        home: prediction?.home ?? null,
        away: prediction?.away ?? null,
      };
    }
    return map;
  };

  const handleLoadMore = async () => {
    if (!id || !nextCursor) return;
    try {
      setLoadingMore(true);
      const response = await fixtureService.getWithPredictions({
        poolId: id,
        pageSize: 120,
        cursorDate: nextCursor.cursorDate,
        cursorId: nextCursor.cursorId,
      });

      if (response.success && response.data) {
        setFixtures((prev) => [...prev, ...response.data!.data]);
        setInputs((prev) => ({ ...prev, ...buildInputs(response.data!.data) }));
        setHasNext(response.data.pagination.hasNext);
        setNextCursor(response.data.pagination.nextCursor ?? null);
      } else {
        Message.error({ content: response.error || "Error al cargar más partidos" });
      }
    } catch (error) {
      Message.error({
        content: error instanceof Error ? error.message : "Error al cargar más partidos",
      });
    } finally {
      setLoadingMore(false);
    }
  };

  const handleScoreChange = (matchId: string, side: "home" | "away", value: number | null) => {
    setInputs((prev) => ({
      ...prev,
      [matchId]: { ...prev[matchId], [side]: value },
    }));
  };

  const handleSave = async (fixture: FixtureWithPredictionResponseDTO) => {
    if (!id) return;
    const input = inputs[fixture.matchId];

    if (input?.home == null || input?.away == null) {
      Message.error({ content: "Ingresa el resultado de ambos equipos" });
      return;
    }

    try {
      setSaving(fixture.matchId);
      const existingId = predictionIds[fixture.matchId];

      const response = existingId
        ? await poolPredictionService.update(existingId, {
            homeScore: input.home,
            awayScore: input.away,
          })
        : await poolPredictionService.create({
            poolId: id,
            fixtureId: fixture.matchId,
            homeScore: input.home,
            awayScore: input.away,
          });

      if (response.success && response.data) {
        if (!existingId) {
          setPredictionIds((prev) => ({ ...prev, [fixture.matchId]: response.data!.id }));
        }
        setFixtures((prev) =>
          prev.map((item) =>
            item.matchId === fixture.matchId
              ? {
                  ...item,
                  userPrediction: {
                    ...item.userPrediction,
                    hasPredicted: true,
                    prediction: { home: input.home!, away: input.away! },
                  },
                }
              : item,
          ),
        );
        Message.success({ content: "Predicción guardada" });
      } else {
        Message.error({ content: response.error || "Error al guardar la predicción" });
      }
    } catch (error) {
      Message.error({
        content: error instanceof Error ? error.message : "Error al guardar la predicción",
      });
    } finally {
      setSaving(null);
    }
  };

  const canViewOthers = (fixture: FixtureWithPredictionResponseDTO) => {
    const privacy = pool?.poolConfig?.privacity?.showOthersPredictions;
    if (privacy === "never") return false;
    if (privacy === "always") return true;
    if (fixture.status.isFinished) return true;
    // "after_lock" (default): visible once predictions are no longer editable
    return !fixture.userPrediction.isEditable;
  };

  const handleViewPredictions = async (fixture: FixtureWithPredictionResponseDTO) => {
    if (!id) return;
    setViewingFixture(fixture);
    setLoadingOthers(true);
    try {
      const response = await poolPredictionService.getByPoolAndFixture(id, fixture.matchId);
      if (response.success && response.data) {
        setOthersPredictions(response.data);
      } else {
        Message.error({ content: response.error || "Error al cargar las predicciones" });
        setOthersPredictions([]);
      }
    } catch (error) {
      Message.error({
        content: error instanceof Error ? error.message : "Error al cargar las predicciones",
      });
      setOthersPredictions([]);
    } finally {
      setLoadingOthers(false);
    }
  };

  const counts = useMemo(() => {
    return {
      live: fixtures.filter((f) => f.status.isInProgress).length,
      pending: fixtures.filter(isPendingFixture).length,
      finished: fixtures.filter((f) => f.status.isFinished).length,
      all: fixtures.length,
    };
  }, [fixtures]);

  const filteredFixtures = useMemo(() => {
    let list: FixtureWithPredictionResponseDTO[];
    switch (activeTab) {
      case "live":
        list = fixtures.filter((f) => f.status.isInProgress);
        break;
      case "pending":
        list = fixtures.filter(isPendingFixture);
        break;
      case "finished":
        list = fixtures.filter((f) => f.status.isFinished);
        break;
      default:
        list = fixtures;
    }
    if (selectedGroupId) {
      list = list.filter((fixture) => fixture.group?.id === selectedGroupId);
    }
    return [...list].sort(sortFixtures);
  }, [fixtures, activeTab, selectedGroupId]);

  const selectedGroupName = useMemo(() => {
    if (!selectedGroupId) return null;
    return groups.find((item) => item.group?.id === selectedGroupId)?.group?.name ?? null;
  }, [groups, selectedGroupId]);

  const handleViewGroupFixtures = (groupId: string) => {
    setSelectedGroupId(groupId);
    setActiveTab("all");
    setMainTab("predictions");
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Button type="link" icon={<FaArrowLeft />} onClick={() => navigate("/sports-predictions/pools/my-pools")}>
        Volver a mis grupos
      </Button>

      <div className="bg-white rounded-lg p-6 shadow-sm">
        <h2 className="text-2xl font-bold text-gray-800">{pool?.name}</h2>
        {pool?.description && <p className="text-gray-600 mt-1">{pool.description}</p>}
        <div className="flex items-center gap-2 mt-3">
          {pool?.tournament && <Tag color="blue">{pool.tournament.name}</Tag>}
          {pool?.season && <Tag color="default">{pool.season.season}</Tag>}
          {pool?.status && (
            <Tag color={pool.status.color || "default"}>{pool.status.name}</Tag>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg p-6 shadow-sm">
        <Tabs
          activeKey={mainTab}
          onChange={(key) => setMainTab(key as MainTab)}
          items={[
            { key: "predictions", label: "Predicciones" },
            { key: "groups", label: "Grupos" },
            { key: "leaderboard", label: "Tabla de posiciones" },
            { key: "rules", label: "Reglas" },
            { key: "comments", label: "Comentarios" },
          ]}
        />

        {mainTab === "groups" && (
          <GroupsTab
            groups={groups}
            loading={loadingGroups}
            onViewFixtures={handleViewGroupFixtures}
          />
        )}
        {mainTab === "leaderboard" && id && <PoolLeaderboardTab poolId={id} />}
        {mainTab === "rules" && <PoolRulesTab poolConfig={pool?.poolConfig} />}
        {mainTab === "comments" && id && <PoolCommentsTab poolId={id} />}

        {mainTab === "predictions" && (
          <>
            {selectedGroupId && (
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-blue-100 bg-blue-50 px-4 py-3">
                <div className="flex items-center gap-2 text-sm font-medium text-blue-800">
                  <FaListOl />
                  <span>Partidos de {selectedGroupName ?? "este grupo"}</span>
                </div>
                <Button size="small" onClick={() => setSelectedGroupId(null)}>
                  Ver todos
                </Button>
              </div>
            )}
            <Tabs
              activeKey={activeTab}
              onChange={(key) => setActiveTab(key as FilterTab)}
              items={[
                { key: "live", label: `En vivo (${counts.live})` },
                { key: "pending", label: `Pendiente (${counts.pending})` },
                { key: "finished", label: `Finalizado (${counts.finished})` },
                { key: "all", label: `Todos (${counts.all})` },
              ]}
            />

        {filteredFixtures.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No hay partidos en esta categoría.</p>
        ) : (
          <div className="space-y-3">
            {filteredFixtures.map((fixture) => {
              const input = inputs[fixture.matchId] ?? { home: null, away: null };
              const { userPrediction } = fixture;
              const isFinished = fixture.status.isFinished;
              const canEdit = userPrediction.isEditable;

              return (
                <Card key={fixture.matchId} className="p-4">
                  <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div className="flex items-center gap-3 flex-1">
                      <FaFutbol className="text-gray-400 hidden md:block" />
                      <div className="flex-1">
                        <div className="flex items-center justify-between gap-4 md:justify-start">
                          <div className="flex items-center gap-2 flex-1 justify-end md:justify-start">
                            <span className="font-medium text-right md:text-left">{fixture.homeTeam.name}</span>
                            {fixture.homeTeam.logo && (
                              <img src={fixture.homeTeam.logo} alt={fixture.homeTeam.name} className="w-6 h-6" />
                            )}
                          </div>
                          <span className="text-gray-400 text-sm">vs</span>
                          <div className="flex items-center gap-2 flex-1">
                            {fixture.awayTeam.logo && (
                              <img src={fixture.awayTeam.logo} alt={fixture.awayTeam.name} className="w-6 h-6" />
                            )}
                            <span className="font-medium">{fixture.awayTeam.name}</span>
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-gray-500">
                          {fixture.date && <span>{new Date(fixture.date).toLocaleString()}</span>}
                          <Tag color={fixture.status.isFinished ? "default" : fixture.status.isInProgress ? "green" : "blue"}>
                            {fixture.status.name}
                          </Tag>
                          {fixture.realResult && (
                            <span className="font-semibold text-gray-700">
                              Resultado: {fixture.realResult.home} - {fixture.realResult.away}
                            </span>
                          )}
                          {userPrediction.hasPredicted && userPrediction.prediction && (
                            <Tag color="purple">
                              Mi predicción: {userPrediction.prediction.home} - {userPrediction.prediction.away}
                            </Tag>
                          )}
                          {userPrediction.scoring && (
                            <Tag color={userPrediction.scoring.totalPoints > 0 ? "gold" : "default"}>
                              <FaStar className="inline mr-1" />
                              +{userPrediction.scoring.totalPoints} pts
                            </Tag>
                          )}
                          {canViewOthers(fixture) && (
                            <Button
                              type="link"
                              size="small"
                              icon={<FaUsers />}
                              onClick={() => handleViewPredictions(fixture)}
                            >
                              Ver predicciones
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <InputNumber
                        min={0}
                        value={input.home}
                        onChange={(value) => handleScoreChange(fixture.matchId, "home", value as number | null)}
                        disabled={!canEdit}
                        className="w-20"
                      />
                      <span className="text-gray-400">-</span>
                      <InputNumber
                        min={0}
                        value={input.away}
                        onChange={(value) => handleScoreChange(fixture.matchId, "away", value as number | null)}
                        disabled={!canEdit}
                        className="w-20"
                      />
                      <Button
                        type="primary"
                        loading={saving === fixture.matchId}
                        disabled={!canEdit}
                        onClick={() => handleSave(fixture)}
                      >
                        {userPrediction.hasPredicted ? "Actualizar" : "Predecir"}
                      </Button>
                    </div>
                  </div>

                  {!canEdit && !isFinished && (
                    <p className="text-xs text-gray-400 mt-2">
                      {userPrediction.isMissed
                        ? "Ya no puedes predecir este partido."
                        : "Las predicciones para este partido están cerradas."}
                    </p>
                  )}
                </Card>
              );
            })}
          </div>
        )}

        {hasNext && (
          <div className="flex justify-center mt-4">
            <Button onClick={handleLoadMore} loading={loadingMore}>
              Cargar más partidos
            </Button>
          </div>
        )}
          </>
        )}
      </div>

      <Modal
        title={
          viewingFixture
            ? `Predicciones - ${viewingFixture.homeTeam.name} vs ${viewingFixture.awayTeam.name}`
            : "Predicciones"
        }
        open={!!viewingFixture}
        onCancel={() => setViewingFixture(null)}
        footer={null}
      >
        {loadingOthers ? (
          <div className="flex justify-center py-6">
            <Spin />
          </div>
        ) : othersPredictions.length === 0 ? (
          <p className="text-gray-500 text-center py-4">Nadie ha registrado una predicción para este partido.</p>
        ) : (
          <div className="divide-y">
            {othersPredictions.map((prediction) => (
              <div key={prediction.id} className="flex items-center justify-between py-2">
                <div>
                  <div className="font-medium">
                    {prediction.username || "Participante"}
                    {prediction.participantId === currentUser?.id && (
                      <span className="text-gray-400"> (Tú)</span>
                    )}
                  </div>
                  {prediction.totalPoints > 0 && (
                    <div className="text-xs text-emerald-600">+{prediction.totalPoints} pts</div>
                  )}
                </div>
                <div className="font-semibold">
                  {prediction.homeScore} - {prediction.awayScore}
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
}

interface GroupsTabProps {
  groups: TournamentStandingGroupedDTO[];
  loading: boolean;
  onViewFixtures: (groupId: string) => void;
}

function GroupsTab({ groups, loading, onViewFixtures }: GroupsTabProps) {
  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Spin />
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <p className="text-gray-500 text-center py-8">
        No hay grupos configurados para esta temporada.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {groups.map((group) => {
        const groupId = group.group?.id;
        const standings = [...group.standings].sort((a, b) => {
          const aRank = a.rank ?? Number.MAX_SAFE_INTEGER;
          const bRank = b.rank ?? Number.MAX_SAFE_INTEGER;
          if (aRank !== bRank) return aRank - bRank;
          return b.points - a.points || b.scoreDiff - a.scoreDiff;
        });

        return (
          <Card key={groupId ?? group.group?.name ?? "sin-grupo"} className="overflow-hidden">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-blue-500" />
                <h3 className="font-semibold text-gray-800">
                  {group.group?.name ?? "Sin grupo"}
                </h3>
              </div>
              {groupId && (
                <Button type="link" size="small" onClick={() => onViewFixtures(groupId)}>
                  Ver partidos
                </Button>
              )}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-sm">
                <thead>
                  <tr className="border-b text-xs text-gray-500">
                    <th className="w-10 py-2 text-left font-medium">#</th>
                    <th className="py-2 text-left font-medium">Equipo</th>
                    <th className="py-2 text-right font-medium">PJ</th>
                    <th className="py-2 text-right font-medium">G</th>
                    <th className="py-2 text-right font-medium">E</th>
                    <th className="py-2 text-right font-medium">P</th>
                    <th className="py-2 text-right font-medium">GF</th>
                    <th className="py-2 text-right font-medium">GC</th>
                    <th className="py-2 text-right font-medium">Dif</th>
                    <th className="py-2 text-right font-medium text-blue-600">Pts</th>
                  </tr>
                </thead>
                <tbody>
                  {standings.map((standing, index) => (
                    <tr key={standing.participantId} className="border-b last:border-b-0">
                      <td className="py-2 text-gray-500">{standing.rank ?? index + 1}</td>
                      <td className="py-2">
                        <div className="flex items-center gap-2">
                          {standing.participantLogo && (
                            <img
                              src={standing.participantLogo}
                              alt={standing.participantName ?? "Equipo"}
                              className="h-5 w-5 rounded-full object-contain"
                            />
                          )}
                          <span className="font-medium text-gray-800">
                            {standing.participantName ?? "Equipo sin nombre"}
                          </span>
                        </div>
                      </td>
                      <td className="py-2 text-right text-gray-600">{standing.played}</td>
                      <td className="py-2 text-right text-emerald-600">{standing.won}</td>
                      <td className="py-2 text-right text-amber-600">{standing.drawn}</td>
                      <td className="py-2 text-right text-red-600">{standing.lost}</td>
                      <td className="py-2 text-right text-gray-600">{standing.scoreFor}</td>
                      <td className="py-2 text-right text-gray-600">{standing.scoreAgainst}</td>
                      <td className="py-2 text-right text-gray-600">
                        {standing.scoreDiff > 0 ? `+${standing.scoreDiff}` : standing.scoreDiff}
                      </td>
                      <td className="py-2 text-right font-semibold text-blue-600">
                        {standing.points}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        );
      })}
    </div>
  );
}

function normalizeGroupedStandings(
  data: TournamentStandingGroupedDTO[] | unknown,
): TournamentStandingGroupedDTO[] {
  if (!Array.isArray(data)) return [];
  return data.filter(
    (item): item is TournamentStandingGroupedDTO =>
      !!item &&
      typeof item === "object" &&
      "standings" in item &&
      Array.isArray((item as TournamentStandingGroupedDTO).standings),
  );
}

function isPendingFixture(fixture: FixtureWithPredictionResponseDTO): boolean {
  return (
    !fixture.userPrediction.hasPredicted &&
    !fixture.userPrediction.isMissed &&
    !fixture.userPrediction.isLockedByAdmin
  );
}

function isToday(date: string | null): boolean {
  if (!date) return false;
  const d = new Date(date);
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

function sortFixtures(a: FixtureWithPredictionResponseDTO, b: FixtureWithPredictionResponseDTO): number {
  if (a.status.isInProgress !== b.status.isInProgress) {
    return a.status.isInProgress ? -1 : 1;
  }
  if (a.status.isFinished !== b.status.isFinished) {
    return a.status.isFinished ? 1 : -1;
  }
  const aTime = a.date ? new Date(a.date).getTime() : 0;
  const bTime = b.date ? new Date(b.date).getTime() : 0;
  if (a.status.isFinished) {
    return bTime - aTime;
  }
  return aTime - bTime;
}
